/**
 * Helper functions to draw basic shapes.
 * And other utils.
 */
import {drawBlockText, measure, TextRun} from "@/canvas/CanvasUtils.ts";
import {Icon} from "anneca-symbols";
import {ICON_FONT} from "@/event/calendar/Constants.ts";
import {Holiday} from "@t/event/CalendarData.ts";

export const MOVE_OPACITY = 0.4;
export const CANCELED_OPACITY = 0.66;
export const DONE_OPACITY = 0.66;
export const DONE_SATURATION = 0.5;

export interface Coordinates {
	x: number;
	y: number;
}

export interface SnapCoordinates extends Coordinates {
	snapX: boolean;
	snapY: boolean;
}

export interface Rect extends Coordinates {
	width: number;
	height: number;
}

export interface Range {
	min?: number,
	max?: number
}

export function applyRange(r:Range, v:number):number {
	if (typeof r.min === 'number' && v < r.min) v = r.min;
	if (typeof r.max === 'number' && v > r.max) v = r.max;
	return v;
}

function minNum(n1: number | undefined, n2: number | undefined):number {
	if (typeof n1 === 'number') {
		if (typeof n2 === 'number') return Math.min(n1, n2);
		return n1;
	} else {
		return n2;
	}
}

function maxNum(n1: number | undefined, n2: number | undefined):number {
	if (typeof n1 === 'number') {
		if (typeof n2 === 'number') return Math.max(n1, n2);
		return n1;
	} else {
		return n2;
	}
}

export function combineRange(r1:Range, r2:Range):Range {
	return { min: minNum(r1.min, r2.min), max: maxNum(r1.max, r2.max) };
}


/** Create new rect which encloses all passed rects */
export function union(...rect:Rect[]):Rect {
	let minX, maxX, minY, maxY;
	let first = true;
	for (let r of rect) {
		if (first) {
			minX = r.x;
			minY = r.y;
			maxX = r.x + r.width;
			maxY = r.y + r.height;
			first = false;
		} else {
			minX = Math.min(minX, r.x);
			minY = Math.min(minY, r.y);
			maxX = Math.max(maxX, r.x + r.width);
			maxY = Math.max(maxY, r.y + r.height);
		}
	}
	return { x: minX, y: minY, width: (maxX - minX), height: (maxY - minY) };
}

export function intersection(r1: Rect, r2: Rect): Rect {
	// Calculate the x and y coordinates of the intersection rectangle
	const x = Math.max(r1.x, r2.x);
	const y = Math.max(r1.y, r2.y);

	// Calculate the width and height of the intersection rectangle
	const width = Math.max(0, Math.min(r1.x + r1.width, r2.x + r2.width) - x);
	const height = Math.max(0, Math.min(r1.y + r1.height, r2.y + r2.height) - y);

	return { x, y, width, height };
}

export function intersectRanges(
	start1: number,
	end1: number,
	start2: number,
	end2: number
): Range | null {
	// Calculate the intersection
	const start = Math.max(start1, start2);
	const end = Math.min(end1, end2);

	// If there is no intersection, return null
	if (start > end) {
		return null;
	}

	return {min: start, max: end};
}


export function pxAlign(value:number, pxRatio:number) {
	return Math.floor(value * pxRatio) / pxRatio;
}

export function insetRect(r:Rect, strokeWidth:number = 1):Rect {
	return {
		x: r.x + (strokeWidth / 2),
		y: r.y + (strokeWidth / 2),
		width: r.width - strokeWidth,
		height: r.height - strokeWidth
	};
}

/** Distance between two points */
export function distance(x1:number, y1:number, x2:number, y2:number):number {
	const w = Math.abs(x1 - x2);
	const h = Math.abs(y1 - y2);
	return Math.sqrt(w * w + h * h);
}

export function drawHoliday(ctx: CanvasRenderingContext2D, h: Holiday, box: Rect, fontFamily:string, defaultColor: string = "#009000") {
	try {
		ctx.save();
		let lineHeight = 20;
		let fontSize = 16;
		let inset = 8;

		if (box.width > 400) {
			fontSize = 32;
			lineHeight = 40;
			inset = 16;
		} else if (box.width > 300) {
			fontSize = 24;
			lineHeight = 30;
			inset = 12;
		} else if (box.width < 150) {
			fontSize = 12;
			lineHeight = 16;
			inset = 6;
		} else if (box.width < 100) {
			fontSize = 10;
			lineHeight = 14;
			inset = 4;
		}

		const runs:TextRun[] = [{ text: h.name, font: ("bold "+fontSize+"px "+fontFamily), color: (h.color ? h.color : defaultColor)+"55" }];
		const ib = insetRect(box, inset);

		drawBlockText(ctx, runs, ib.x, ib.y, ib.width, ib.height, lineHeight, "center", "bottom");
	} finally {
		ctx.restore();
	}
}

export function drawTextBadge(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, hpadding: number, vpadding: number, bg: string|null, fg: string, suffix = ""): Rect {
	const m = measure(ctx, text);
	const totalTextHeight = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent;
	// Box containing the pill
	const pill: Rect = {
		x: x - hpadding,
		y: y - m.fontBoundingBoxAscent - vpadding,
		width: m.width + 2 * hpadding,
		height: totalTextHeight + 2 * vpadding
	};
	if (bg) {
		ctx.fillStyle = bg;
		// Start drawing the pill shape
		ctx.beginPath();
		const radius = pill.height / 2;
		// Start at the left middle of the rectangle
		ctx.moveTo(pill.x + radius, pill.y);

		// Top right corner arc
		ctx.arcTo(pill.x + pill.width, pill.y, pill.x + pill.width, pill.y + radius, radius);

		// Bottom right corner arc
		ctx.arcTo(pill.x + pill.width, pill.y + pill.height, pill.x + pill.width - radius, pill.y + pill.height, radius);

		// Bottom left corner arc
		ctx.arcTo(pill.x, pill.y + pill.height, pill.x, pill.y + pill.height - radius, radius);

		// Top left corner arc
		ctx.arcTo(pill.x, pill.y, pill.x + radius, pill.y, radius);

		// Close the path
		ctx.closePath();
		ctx.fill();
	}
	ctx.fillStyle = fg;
	ctx.fillText(text + suffix, x, y);
	return pill;
}

function correctRadiuses(r1:number, r2:number, w:number, setter: (r1:number, r2:number) => void):void {
	if (w === 0) setter(0, 0);
	else if (r1 + r2 > w) {
		if (r2 === 0) setter(Math.max(w, 0), 0);
		else {
			const ratio = r1 / r2;
			const cr1 = ratio * w;
			const cr2 = w - cr1;
			setter(Math.max(cr1, 0), Math.max(cr2, 0));
		}
	}
	if (r1 < 0 || r2 < 0) {
		setter(Math.max(r1, 0), Math.max(r2, 0));
	}
}

/**
 * Create shape with 0..4 round corners suitable for filling
 * @param ctx drawing context
 * @param r outer rectangle (border box)
 * @param radiusTopLeft
 * @param radiusTopRight
 * @param radiusBottomLeft
 * @param radiusBottomRight
 */
export function roundRectShape(r:Rect,
							   radiusTopLeft:number, radiusTopRight:number,
							   radiusBottomRight:number, radiusBottomLeft:number, p:Path2D|CanvasRenderingContext2D):void {

	// Check and normalize radiuses
	correctRadiuses(radiusTopLeft, radiusBottomLeft, r.height, (r1:number, r2:number) => {
		radiusTopLeft = r1;
		radiusBottomLeft = r2;
	});
	correctRadiuses(radiusTopRight, radiusBottomRight, r.height, (r1:number, r2:number) => {
		radiusTopRight = r1;
		radiusBottomRight = r2;
	});
	correctRadiuses(radiusTopLeft, radiusTopRight, r.width, (r1:number, r2:number) => {
		radiusTopLeft = r1;
		radiusTopRight = r2;
	});
	correctRadiuses(radiusBottomLeft, radiusBottomRight, r.width, (r1:number, r2:number) => {
		radiusBottomLeft = r1;
		radiusBottomRight = r2;
	});

	// Start at the top left corner
	p.moveTo(r.x + radiusTopLeft, r.y);
	// Top right corner arc
	p.arcTo(r.x + r.width, r.y, r.x + r.width, r.y + radiusTopRight, radiusTopRight);
	// Bottom right corner arc
	p.arcTo(r.x + r.width, r.y + r.height, r.x + r.width - radiusBottomRight, r.y + r.height, radiusBottomRight);
	// Bottom left corner arc
	p.arcTo(r.x, r.y + r.height, r.x, r.y + r.height - radiusBottomLeft, radiusBottomLeft);
	// Top left corner arc
	p.arcTo(r.x, r.y, r.x + radiusTopLeft, r.y, radiusTopLeft);
	// Close the path
	p.closePath();
}

/**
 * Create shape with 0..4 round corners suitable for filling
 * @param ctx drawing context
 * @param r outer rectangle (border box)
 * @param radiusTopLeft
 * @param radiusTopRight
 * @param radiusBottomLeft
 * @param radiusBottomRight
 */
export function roundRect(r:Rect,
							   radiusTopLeft:number, radiusTopRight:number,
							   radiusBottomRight:number, radiusBottomLeft:number):Path2D {

	const p = new Path2D();
	roundRectShape(r, radiusTopLeft, radiusTopRight, radiusBottomRight, radiusBottomLeft, p);
	return p;
}
/**
 * Create shape with 0..4 round corners suitable for filling
 * @param ctx drawing context
 * @param r outer rectangle (border box)
 * @param radiusTopLeft
 * @param radiusTopRight
 * @param radiusBottomLeft
 * @param radiusBottomRight
 */
export function roundFillShape(ctx: CanvasRenderingContext2D, r:Rect,
							   radiusTopLeft:number, radiusTopRight:number,
							   radiusBottomRight:number, radiusBottomLeft:number) {

	ctx.beginPath();
	roundRectShape(r, radiusTopLeft, radiusTopRight, radiusBottomRight, radiusBottomLeft, ctx);
}

export interface IconTextMeasure {
	/** Total width */
	width: number;
	iconWidth: number;
	textWidth: number;
	icon: string|null;
	text: string|null;
	ascent: number;
	descent: number;
}

export function decodeNumericIconEntity(entity:string):string {
	if (entity.startsWith('&#x')) {
		return String.fromCodePoint(parseInt(entity.slice(3, -1), 16));
	} else if (entity.startsWith('&#')) {
		return String.fromCodePoint(parseInt(entity.slice(2, -1), 10));
	}
	return entity;
}

const SPACE_RATIO = 4 / 14;

/** Calculate position of icon and text and total width */
export function measureIconText(ctx: CanvasRenderingContext2D, text: string|null|undefined, icon: string|null|undefined, textFontFamily:string, fontSize = 14):IconTextMeasure {
	const itm:IconTextMeasure = { ascent: 0, descent: 0, iconWidth: 0, textWidth: 0, width: 0, icon:null, text: null };
	if (icon) {
		ctx.font = fontSize + "px " + ICON_FONT;
		const pi = Icon.parse(icon);
		const iconText = decodeNumericIconEntity(pi.entity);
		itm.icon = iconText;
		const iconMeasure = ctx.measureText(iconText);
		itm.ascent = iconMeasure.fontBoundingBoxAscent;
		itm.descent = iconMeasure.fontBoundingBoxDescent;
		itm.iconWidth = itm.width = iconMeasure.width;
	}
	if (text) {
		ctx.font = fontSize+"px "+textFontFamily;
		const textMeasure = ctx.measureText(text);
		itm.text = text;
		itm.ascent = Math.max(textMeasure.fontBoundingBoxAscent, itm.ascent);
		itm.descent = Math.max(textMeasure.fontBoundingBoxDescent, itm.descent);
		itm.textWidth = textMeasure.width;
		itm.width += textMeasure.width;
	}
	if (icon && text) {
		// Both are specified a space must be added
		const spaceWidth = Math.round(SPACE_RATIO * fontSize);
		itm.iconWidth += spaceWidth;
		itm.width += spaceWidth;
	}
	return itm;
}

export interface Slot {
	/** Start of the slot (left || top) */
	start: number;
	/** Size of the slot (width || height) */
	size: number;
	/** Center of the gap (to be easily drawn by moveTo-lineTo) */
	gapLine: number;
}

/**
 * Calculate distributionof visible pixels into certain number of slots and gap.
 * @param visiblePixels how much space are going to be distributed
 * @param slots number of visible slots (integer > 0)
 * @param gap width of the gap (integer >= 0)
 *
 * @returns distributed slots or null if it cannot be done (not enough space)
 */
export function distributeGridSpace(visiblePixels: number, slots: number, gap: number = 1):Slot[]|null {
	if (slots < 1) slots = 1;
	const MIN_REQ = slots + (slots - 1) * gap;	// minimum required to generate 1px slots
	if (visiblePixels < MIN_REQ) visiblePixels = MIN_REQ;
	let space = Math.floor(visiblePixels - (slots - 1) * gap);	// distributable space
	let size = space / slots;
	const s:Slot[] = [];
	let start = 0;
	for (let i = 0; i < slots; i++) {
		if (i === (slots - 1)) size = space;
		else size = Math.round(space / (slots - i));
		s.push({
			start,
			size,
			gapLine: (start + size + gap / 2)
		});
		space -= size;
		start += size + gap;
	}
	return s;
}


/**** Shadows ****/

export function drawWithShadow(ctx: CanvasRenderingContext2D, offsetX:number, offsetY:number, blur:number, color:string, draw:(ctx:CanvasRenderingContext2D) => void):void {
	try {
		ctx.save();
		ctx.shadowColor = color;
		ctx.shadowBlur = blur;
		ctx.shadowOffsetX = offsetX;
		ctx.shadowOffsetY = offsetY;
		draw(ctx);
	} finally {
		ctx.restore();
	}
}

export function fillInnerShadow(ctx: CanvasRenderingContext2D, outerRect:Rect, shape:Path2D, offsetX:number, offsetY:number, blur:number, color:string):void {

	const composed = new Path2D();
	composed.rect(outerRect.x, outerRect.y, outerRect.width, outerRect.height);
	composed.addPath(shape);

	try {
		ctx.save();
		ctx.clip(shape);
		ctx.shadowColor = color;
		ctx.shadowBlur = blur;
		ctx.shadowOffsetX = offsetX;
		ctx.shadowOffsetY = offsetY;
		ctx.fillStyle = "green";
		ctx.fill(composed, "evenodd");
	} finally {
		ctx.restore();
	}
}

export interface RGB {
	r: number,
	g: number,
	b: number
}

export interface RGBA extends RGB {
	a?: number
}

export function rgbToHex({ r, g, b, a }: RGBA): string {
	// Ověření rozsahu hodnot
	if (![r, g, b].every(v => v >= 0 && v <= 255)) {
		throw new Error("RGB values must be in the range 0-255.");
	}
	if (a !== undefined && (a < 0 || a > 1)) {
		throw new Error("Alpha value must be in the range 0-1.");
	}

	// Převede čísla na dvouciferné hex hodnoty
	const toHex = (v: number) => v.toString(16).padStart(2, "0").toUpperCase();

	const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

	// Pokud je alfa definovaná, přidá ji jako dvoucifernou hex hodnotu
	return a !== undefined ? `${hex}${toHex(Math.round(a * 255))}` : hex;
}

export function parseHexColor(hex: string): RGBA {
	// Ověření vstupu
	if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(hex)) {
		return { r: 0, g: 0, b: 0 };
	}

	// Parsování hodnot
	const r = parseInt(hex.substring(1, 3), 16);
	const g = parseInt(hex.substring(3, 5), 16);
	const b = parseInt(hex.substring(5, 7), 16);
	const a = hex.length === 9 ? parseInt(hex.substring(7, 9), 16) / 255 : undefined;

	return a !== undefined ? { r, g, b, a } : { r, g, b };
}

export function reduceSaturation(r: number, g: number, b: number, amount: number): RGB {
	// Výpočet grayscale hodnoty pomocí váženého průměru
	const gray = r * 0.299 + g * 0.587 + b * 0.114;

	// Interpolace mezi původní barvou a grayscale podle amount
	return {
		r: Math.round(r + (gray - r) * amount),
		g: Math.round(g + (gray - g) * amount),
		b: Math.round(b + (gray - b) * amount)
	};
}


export function luminance(r: number, g: number, b: number): number {
	const a = [r, g, b].map(v => {
		v /= 255;
		return v <= 0.03928
			? v / 12.92
			: Math.pow((v + 0.055) / 1.055, 2.4);
	});
	return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function contrast(rgb1: RGB, rgb2: RGB): number {
	const lum1 = luminance(rgb1.r, rgb1.g, rgb1.b);
	const lum2 = luminance(rgb2.r, rgb2.g, rgb2.b);
	const brightest = Math.max(lum1, lum2);
	const darkest = Math.min(lum1, lum2);
	return (brightest + 0.05) / (darkest + 0.05);
}

export function grayscale(rgb: RGB):number {
	return Math.round(rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114);
}